import {
  InfiniteListBase,
  useCreatePath,
  useGetIdentity,
  useListContext,
  useTranslate,
} from "ra-core";
import { Link, matchPath, useLocation } from "react-router";

import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { InfinitePagination } from "../misc/InfinitePagination";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal } from "../types";
import { DealCreate } from "./DealCreate";
import { DealEdit } from "./DealEdit";
import { DealShow } from "./DealShow";
import { ReplyClockBadge } from "./ReplyClockBadge";
import { findDealLabel } from "./dealUtils";

/**
 * Phone layout of the enquiries: a newest-first (by Received) list of open
 * enquiries with a "New enquiry" button. Archived enquiries are excluded, as
 * on the desktop board. Rows open the same show / edit / create dialogs.
 */
export const DealListMobile = () => {
  const { identity } = useGetIdentity();
  if (!identity) return null;

  return (
    <InfiniteListBase
      perPage={25}
      sort={{ field: "received_at", order: "DESC" }}
      filter={{ "archived_at@is": null }}
    >
      <DealListLayoutMobile />
    </InfiniteListBase>
  );
};

const DealListLayoutMobile = () => {
  const translate = useTranslate();
  const location = useLocation();
  const { error } = useListContext<Deal>();
  const matchCreate = matchPath("/deals/create", location.pathname);
  const matchShow = matchPath("/deals/:id/show", location.pathname);
  const matchEdit = matchPath("/deals/:id", location.pathname);

  return (
    <div>
      <MobileHeader showRefresh>
        <h1 className="text-xl font-semibold">
          {translate("resources.deals.name", { smart_count: 2 })}
        </h1>
      </MobileHeader>
      <MobileContent>
        <div className="flex flex-col gap-3">
          <Button asChild className="h-12 text-base">
            <Link to="/deals/create">
              {translate("resources.deals.action.new")}
            </Link>
          </Button>
          <DealRows />
          {!error && (
            <div className="flex justify-center">
              <InfinitePagination />
            </div>
          )}
        </div>
      </MobileContent>
      <DealCreate open={!!matchCreate} />
      <DealEdit open={!!matchEdit && !matchCreate} id={matchEdit?.params.id} />
      <DealShow open={!!matchShow} id={matchShow?.params.id} />
    </div>
  );
};

const DealRows = () => {
  const translate = useTranslate();
  const createPath = useCreatePath();
  const { dealStages } = useConfigurationContext();
  const { data, isPending } = useListContext<Deal>();

  if (isPending) return null;
  if (!data?.length) {
    return (
      <p className="text-sm text-muted-foreground text-center py-6">
        {translate("resources.deals.empty.title")}
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {data.map((deal) => (
        <li key={deal.id}>
          <Link
            to={createPath({ resource: "deals", id: deal.id, type: "show" })}
            className="block"
          >
            <Card className="p-3 gap-1">
              <div className="flex items-center justify-between gap-2">
                <span
                  className="font-mono text-xs text-muted-foreground"
                  aria-label={translate("resources.deals.fields.job_number")}
                >
                  {deal.job_number}
                </span>
                <ReplyClockBadge deal={deal} />
              </div>
              <span className="font-medium truncate">{deal.name}</span>
              <div className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
                <ReferenceField
                  source="company_id"
                  reference="companies"
                  record={deal}
                  link={false}
                >
                  <TextField source="name" />
                </ReferenceField>
                <span>{findDealLabel(dealStages, deal.stage)}</span>
              </div>
            </Card>
          </Link>
        </li>
      ))}
    </ul>
  );
};
